/**
 * SendToPrintModal — Universal "Send to Print" dialog (unified picker, 2026-08-10;
 * print-device unification 2026-08-11).
 *
 * Two-step flow (owner-approved): pick FORMAT → pick DESTINATION.
 * Destinations are print devices (/print-receivers) only — paired receiver apps,
 * both device_kind "station" and "terminal". Jobs are reference-based: no HTML
 * is sent; the device pulls render data and prints silently on the mapped
 * physical printer. Devices without the chosen format's printer are shown but
 * not selectable.
 *
 * Props:
 *   open           boolean
 *   onOpenChange   fn(bool)
 *   documentType   string  doc type, e.g. "sales_receipt" — label-only now (DOC_TYPE_LABELS)
 *   receiverDocType string — pull-allowlist type ("invoice" | "purchase_order" |
 *                  "branch_transfer" | "proof_of_payment" | "expense" |
 *                  "stock_request"). Omit at a
 *                  site that is not reference-ready → no devices offered there.
 *   documentName   string  e.g. "Sales Receipt #INV-001"
 *   documentId     string
 *   docCode        string  optional short doc code (alternative to documentId)
 *   formats        string[] optional — restrict the format cards (e.g. ['thermal'] for a
 *                  thermal-only report strip); default = every format
 *   documentByFormat {thermal?: {documentId, documentName}, dot_matrix?: {...}} optional —
 *                  when each paper prints a DIFFERENT stored document (a report
 *                  snapshot per layout), the chosen format picks the job target;
 *                  its keys also define the offered formats
 *   referenceNumber string
 *   branchId       string  — used to fetch matching print devices
 *   htmlContent    string  — LEGACY/no-op: devices pull render data by reference
 *   buildHtml      fn(format) → string — LEGACY/no-op (kept: ~20 call sites still pass it)
 *   metadata       object  — any extra data to attach
 *   axiosInstance  — optional, used by terminal surfaces (outside AuthContext)
 *   compact        boolean — tighter layout for the handheld terminal
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Printer, Wifi, WifiOff, AlertTriangle, Clock, CheckCircle2, RefreshCw, Building2, Loader2, MonitorSmartphone } from 'lucide-react';
import { api as defaultApi } from '../contexts/AuthContext';
import { toast } from 'sonner';

const DOC_TYPE_LABELS = {
  sales_receipt:    'Sales Receipt',
  order_slip:       'Order Slip',
  trust_receipt:    'Trust Receipt',
  official_receipt: 'Official Receipt',
  purchase_order:   'Purchase Order',
  z_report:         'Z-Report',
  advance_z_report: 'Advance Z-Report',
  branch_transfer:  'Branch Transfer',
  stock_request:    'Stock Request',
  expense_receipt:  'Expense Receipt',
  expense_voucher:  'Expense Voucher',
  return_receipt:   'Return/Refund Receipt',
  return_slip:      'Return Slip',
  proof_of_payment: 'Proof of Payment',
  statement:        'Customer Statement',
  packing_slip:     'Packing Slip',
  capital_change_report: 'Capital Change Report',
};

// Data-driven so a future "document" (A4/color) format is one entry here +
// one entry in the backend's VALID_FORMATS (Tier B slot, parked 2026-08-10).
const FORMATS = [
  { id: 'thermal',    label: 'Thermal',            hint: 'Receipt roll' },
  { id: 'dot_matrix', label: 'Dot Matrix',         hint: '8.5×11 continuous' },
];

export default function SendToPrintModal({
  open, onOpenChange,
  documentType = 'sales_receipt',
  receiverDocType = '',
  documentName = '',
  documentId   = '',
  docCode      = '',
  referenceNumber = '',
  branchId     = '',
  htmlContent  = '',   // legacy/no-op — device pulls render data by reference
  buildHtml    = null, // legacy/no-op — kept so existing call sites keep working
  metadata     = {},
  formats      = null, // e.g. ['thermal'] — see header comment
  documentByFormat = null, // { format: {documentId, documentName} } — see header comment
  axiosInstance,   // optional — used by terminal (outside AuthContext)
  compact      = false,
}) {
  const api = axiosInstance || defaultApi;
  // Allowed format cards — a report snapshot has ONE layout (strip = thermal,
  // sheet = dot matrix), so its sender pins the list to that format.
  const formatsKey = Array.isArray(formats) ? formats.join(',')
    : (documentByFormat ? Object.keys(documentByFormat).filter(k => documentByFormat[k]).join(',') : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const FORMAT_LIST = useMemo(() => (formatsKey ? FORMATS.filter(fm => formatsKey.split(',').includes(fm.id)) : FORMATS), [formatsKey]);
  const [format, setFormat]         = useState(FORMAT_LIST[0]?.id || 'thermal');
  // The job target for the chosen paper (a report snapshot per layout) — falls
  // back to the single documentId/documentName every other caller passes.
  const target = (documentByFormat && documentByFormat[format]) || { documentId, documentName };
  const [stations, setStations]     = useState([]);
  // selected = { kind: 'station', id }
  const [selected, setSelected]     = useState(null);
  // { station, from, to } — set when picking a station forced a format switch
  const [autoSwitched, setAutoSwitched] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [sending, setSending]       = useState(false);
  const [sent, setSent]             = useState(false);
  const [result, setResult]         = useState(null);
  const [copies, setCopies]         = useState(1);
  // live tracking of a station job after Send: {jobId, status, pages, error, receiverOnline, startedAt}
  const [track, setTrack]           = useState(null);

  const stationSupports = useCallback((s, fmt) => {
    const caps = s.capabilities || {};
    return caps[fmt] !== false;   // unknown/unset = allowed (server enforces too)
  }, []);

  // Smart pick: choosing a station that lacks the current format switches the
  // format to one the station has (owner ask 2026-08-10) — the user still
  // confirms with Send, nothing fires automatically.
  const pickStation = (s) => {
    if (stationSupports(s, format)) {
      setSelected({ kind: 'station', id: s.id });
      setAutoSwitched(null);
      return;
    }
    const alt = FORMAT_LIST.find(fm => stationSupports(s, fm.id));
    if (!alt) return;   // supports nothing — button stays disabled anyway
    setFormat(alt.id);
    setSelected({ kind: 'station', id: s.id });
    setAutoSwitched({
      station: s.name || 'This station',
      from: FORMAT_LIST.find(fm => fm.id === format)?.label || format,
      to: alt.label,
    });
  };

  const fetchDestinations = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    const wantStations = !!receiverDocType && (!!documentId || !!docCode || !!documentByFormat);
    try {
      const stationRes = wantStations
        ? await api.get(`/print-receivers?branch_id=${encodeURIComponent(branchId)}`).catch(() => ({ data: [] }))
        : { data: [] };
      const stationList = (stationRes.data || []).filter(s => s.status === 'active');
      setStations(stationList);
    } catch {
      toast.error('Could not fetch print destinations');
    }
    setLoading(false);
  }, [branchId, receiverDocType, documentId, docCode, documentByFormat]);

  // Auto-select the best destination: online device supporting the format,
  // then any device supporting it.
  useEffect(() => {
    const okStations = stations.filter(s => stationSupports(s, format));
    const pick =
      okStations.find(s => s.is_online) ? { kind: 'station', id: okStations.find(s => s.is_online).id } :
      okStations[0]                     ? { kind: 'station', id: okStations[0].id } :
      null;
    setSelected(prev => {
      // keep a still-valid manual choice
      if (prev?.kind === 'station' && okStations.some(s => s.id === prev.id)) return prev;
      return pick;
    });
  }, [stations, format, stationSupports]);

  useEffect(() => {
    if (open) {
      setSent(false);
      setResult(null);
      setAutoSwitched(null);
      setTrack(null);
      setCopies(1);
      setFormat(prev => (FORMAT_LIST.some(fm => fm.id === prev) ? prev : (FORMAT_LIST[0]?.id || 'thermal')));
      fetchDestinations();
    }
  }, [open, fetchDestinations, FORMAT_LIST]);

  // Live station-job tracking: poll until printed/failed (or give up after 5 min —
  // a long dot-matrix batch keeps printing; the note tells the user it's safe to close).
  useEffect(() => {
    if (!open || !track?.jobId || ['printed', 'failed'].includes(track.status)) return;
    if (Date.now() - track.startedAt > 5 * 60 * 1000) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/print-receivers/jobs/${track.jobId}/status`);
        setTrack(prev => prev && {
          ...prev,
          status: r.data.status,
          pages: r.data.pages_printed || 0,
          error: r.data.error_message || '',
          receiverOnline: r.data.receiver_online,
        });
      } catch { /* keep last known state; retry next tick */ }
    }, 2000);
    return () => clearTimeout(t);
  }, [open, track]);

  const handleSend = async () => {
    if (!selected) { toast.error('Please select a print destination'); return; }
    setSending(true);
    try {
      const res = await api.post('/print-receivers/jobs', {
        receiver_id:      selected.id,
        print_format:     format,
        document_type:    receiverDocType,
        document_id:      target.documentId,
        doc_code:         docCode,
        document_name:    target.documentName,
        reference_number: referenceNumber,
        // DM prints on multi-ply carbon paper — the plies ARE the copies
        // (owner 2026-08-11); server enforces the same rule
        copies:           format === 'dot_matrix' ? 1 : copies,
        metadata,
      });
      setResult({ ...res.data, kind: 'station' });
      setSent(true);
      setTrack({
        jobId: res.data.job_id,
        status: res.data.receiver_online === false ? 'pending' : 'sent_waiting',
        pages: 0, error: '', receiverOnline: res.data.receiver_online,
        startedAt: Date.now(),
      });
      toast.success(res.data.message || 'Print job sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send print job');
    }
    setSending(false);
  };

  const selectedStation  = selected?.kind === 'station'  ? stations.find(s => s.id === selected.id) : null;
  const selectedOffline  = selectedStation && !selectedStation.is_online;
  const nothingAvailable = !loading && stations.length === 0;

  const pad = compact ? 'p-2.5' : 'p-3';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={compact ? 'max-w-sm' : 'max-w-md'} data-testid="send-to-print-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Printer size={16} className="text-emerald-600" />
            Send to Print
          </DialogTitle>
        </DialogHeader>

        {/* Document info */}
        <div className={`bg-slate-50 rounded-lg ${pad} text-sm mb-1`}>
          <p className="font-semibold text-slate-800 truncate">{target.documentName || DOC_TYPE_LABELS[documentType] || documentType}</p>
          {referenceNumber && (
            <p className="text-xs text-slate-500 mt-0.5">Ref: {referenceNumber}</p>
          )}
          <Badge variant="secondary" className="mt-1.5 text-[10px]">
            {DOC_TYPE_LABELS[documentType] || documentType}
          </Badge>
        </div>

        {sent ? (
          <div className="text-center py-6 space-y-3" data-testid="print-sent-state">
            {result?.kind === 'station' && track ? (
              track.status === 'printed' ? (
                <>
                  <CheckCircle2 size={36} className="text-emerald-500 mx-auto" />
                  <p className="font-semibold text-slate-800" data-testid="track-printed">Printed ✓</p>
                  {track.pages > 0 && <p className="text-xs text-slate-500">{track.pages} page{track.pages > 1 ? 's' : ''}</p>}
                </>
              ) : track.status === 'failed' ? (
                <>
                  <AlertTriangle size={36} className="text-red-500 mx-auto" />
                  <p className="font-semibold text-slate-800" data-testid="track-failed">Print failed</p>
                  <p className="text-xs text-red-600">{track.error || 'The station reported a failure.'}</p>
                  <p className="text-xs text-slate-500">Fix the printer and send again — or use this page's local Print buttons instead.</p>
                </>
              ) : (
                <>
                  <Loader2 size={36} className="text-emerald-500 mx-auto animate-spin" />
                  <p className="font-semibold text-slate-800" data-testid="track-progress">
                    {track.receiverOnline === false
                      ? 'Queued — station is offline'
                      : track.status === 'claimed'
                        ? `Printing…${track.pages > 0 ? ` (page ${track.pages})` : ''}`
                        : 'Waiting for the station…'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {track.receiverOnline === false
                      ? 'It will print automatically when the station reconnects.'
                      : Date.now() - track.startedAt > 30000
                        ? 'Long documents keep printing after you close this window.'
                        : 'This updates live — most receipts land in a few seconds.'}
                  </p>
                </>
              )
            ) : (
              <>
                <CheckCircle2 size={36} className="text-emerald-500 mx-auto" />
                <p className="font-semibold text-slate-800">Print job sent!</p>
                <p className="text-xs text-slate-500">The print device will print your document shortly.</p>
              </>
            )}
            <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <>
            {/* Step 1 — format */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Print Format</p>
              <div className="flex gap-2">
                {FORMAT_LIST.map(fm => (
                  <button
                    key={fm.id}
                    data-testid={`print-format-${fm.id}`}
                    onClick={() => { setFormat(fm.id); setAutoSwitched(null); }}
                    className={`flex-1 rounded-lg border px-3 py-2 text-left transition-all ${
                      format === fm.id
                        ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <p className="text-sm font-semibold text-slate-800">{fm.label}</p>
                    <p className="text-[10px] text-slate-500">{fm.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2 — destination */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Print Destination
              </p>

              {loading ? (
                <div className="flex items-center justify-center py-6 text-slate-400">
                  <Loader2 size={18} className="animate-spin mr-2" /> Loading destinations...
                </div>
              ) : nothingAvailable ? (
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700 border border-amber-200">
                  <AlertTriangle size={15} />
                  <span>
                    No print devices for this branch.
                    <br />Pair one in Print Center.
                  </span>
                </div>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {stations.map(s => {
                    const supports = stationSupports(s, format);
                    const supportsAny = FORMATS.some(fm => stationSupports(s, fm.id));
                    const isSel = selected?.kind === 'station' && selected.id === s.id;
                    const KindIcon = s.device_kind === 'terminal' ? MonitorSmartphone : Printer;
                    return (
                      <button
                        key={s.id}
                        data-testid={`station-option-${s.id}`}
                        onClick={() => pickStation(s)}
                        disabled={!supportsAny}
                        title={supports ? '' : supportsAny
                          ? `No ${format === 'thermal' ? 'thermal' : 'dot matrix'} printer here — picking it switches the format`
                          : 'No printers set up on this station yet'}
                        className={`w-full flex items-center gap-3 ${pad} rounded-xl border text-left transition-all ${
                          isSel
                            ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300'
                            : supportsAny
                              ? 'border-slate-200 hover:border-slate-300 bg-white'
                              : 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          s.is_online ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
                        }`}>
                          <KindIcon size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{s.name || 'Print Station'}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Building2 size={10} />
                            {s.branch_name || '—'}
                            <span className="text-[9px] text-slate-400 ml-1">
                              {s.device_kind === 'terminal' ? 'Terminal' : 'Station'}
                            </span>
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`flex items-center gap-0.5 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                            s.is_online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {s.is_online ? <Wifi size={9} /> : <WifiOff size={9} />}
                            {s.is_online ? 'Online' : 'Offline'}
                          </span>
                          {s.pending_jobs > 0 && (
                            <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                              {s.pending_jobs} pending
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}

                </div>
              )}
            </div>

            {/* Copies — station jobs only (server caps at 5; DM is always 1:
                the 3-ply carbon paper IS the duplicates, owner 2026-08-11) */}
            {selected?.kind === 'station' && format !== 'dot_matrix' && (
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Copies</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setCopies(c => Math.max(1, c - 1))} disabled={copies <= 1}
                    className="w-7 h-7 rounded-lg border border-slate-300 text-slate-600 font-bold disabled:opacity-40"
                    data-testid="copies-minus">−</button>
                  <span className="w-6 text-center text-sm font-bold text-slate-800" data-testid="copies-value">{copies}</span>
                  <button onClick={() => setCopies(c => Math.min(5, c + 1))} disabled={copies >= 5}
                    className="w-7 h-7 rounded-lg border border-slate-300 text-slate-600 font-bold disabled:opacity-40"
                    data-testid="copies-plus">+</button>
                </div>
              </div>
            )}
            {selected?.kind === 'station' && format === 'dot_matrix' && (
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Copies</p>
                <p className="text-xs text-slate-500" data-testid="copies-dm-note">
                  1 per print — the multi-ply paper makes the duplicates
                </p>
              </div>
            )}

            {/* Auto-switched format note */}
            {autoSwitched && (
              <div className="flex items-start gap-2 p-2.5 bg-sky-50 rounded-lg text-xs text-sky-700 border border-sky-200"
                data-testid="format-autoswitch-note">
                <Printer size={13} className="mt-0.5 shrink-0" />
                {autoSwitched.station} has no {autoSwitched.from} printer — switched to <b>{autoSwitched.to}</b>. Press Send to print.
              </div>
            )}

            {/* Offline note */}
            {selectedOffline && (
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 rounded-lg text-xs text-amber-700 border border-amber-200">
                <Clock size={13} className="mt-0.5 shrink-0" />
                This destination is offline. Your job will be queued and delivered automatically when it comes back online.
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
                data-testid="send-to-print-cancel"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                onClick={handleSend}
                disabled={sending || !selected || nothingAvailable}
                data-testid="send-to-print-confirm"
              >
                {sending ? (
                  <><Loader2 size={14} className="animate-spin mr-1.5" /> Sending...</>
                ) : (
                  <><Printer size={14} className="mr-1.5" /> Send to Print</>
                )}
              </Button>
            </div>

            <button
              className="w-full text-xs text-slate-400 hover:text-slate-600 mt-1"
              onClick={fetchDestinations}
              data-testid="refresh-devices-btn"
            >
              <RefreshCw size={10} className="inline mr-1" /> Refresh destinations
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
